import React, { useState, useEffect, useCallback, useMemo, useRef, useId } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { COURSE_DETAILS } from "./CourseDetail";
import { workbookService } from '../services/workbookService';
import WorkbookPurchaseModal from '../components/Workbook/WorkbookPurchaseModal';
import { getWorkbookPricing } from '../data/workbookCatalog';
import { engine } from '../core/books/storage.js';
import dataEngine from '../core/books/data.js';
import { getDynamicFreeStudySections } from '../data/freeStudyData'; // 🆕
import { useAdminUploads } from '../hooks/useAdminUploads';
import "./books.css";


// تابع کمکی برای نمایش مدت کل
import { FREE_STUDY_GROUPS } from '../data/freeStudyData';

// 🔽 CHANGED: curriculum helpers
import {
  GRADE_LIST,
  FIELD_LIST,
  GRADE_TITLES as CUR_GRADE_TITLES,
  getSubjectsFor,
  getSubject as getSubjectMeta,
  getSubjectTitle,
  needsField as curriculumNeedsField,
  normalizeGradeId as curriculumNormalizeGrade,
  normalizeFieldId as curriculumNormalizeField,
} from '../core/education/curriculum.js';

// تابع کمکی برای نمایش مدت کل
const formatTotalDuration = (videos) => {
  const total = (videos || []).reduce((s, v) => s + (Number(v.duration) || 0), 0);
  if (total === 0) return '—';
  if (total < 60) return `${total} دقیقه`;
  const h = Math.floor(total / 60);
  const m = total % 60;
  return m === 0 ? `${h} ساعت` : `${h}:${String(m).padStart(2, '0')} ساعت`;
};

// تابع محاسبه پیشرفت واقعی مطالعه آزاد
const computeFreeStudyProgress = (userId, courseId, totalVideos) => {
  if (!userId || userId === 'unknown' || !courseId || !totalVideos) return 0;
  try {
    // 🆕 اول کلید v2، بعد کلید قدیمی
    const candidates = [
      `freestudy-progress-v2-${userId}-${courseId}`,
      `freestudy-progress-${userId}-${courseId}`,
    ];
    for (const key of candidates) {
      // 🆕 از engine (هماهنگ با remoteSync)
      const parsed = engine.get(key, null);
      if (!parsed) continue;
      const list =
        Array.isArray(parsed.completedVideoIds) && parsed.completedVideoIds.length > 0
          ? parsed.completedVideoIds
          : Array.isArray(parsed.completedVideos)
            ? parsed.completedVideos
            : [];
      return Math.min(100, Math.round((list.length / totalVideos) * 100));
    }
    return 0;
  } catch {
    return 0;
  }
};

// ================================================================
// Helper: نرمال‌سازی پایه تحصیلی به شماره (هماهنگ با StudentHome)
// ================================================================
// 🔽 CHANGED: delegate به curriculum — پشتیبانی از ۱ تا ۱۲
const normalizeGrade = (grade) => curriculumNormalizeGrade(grade);

// 🔽 CHANGED: عناوین پایه از curriculum
const GRADE_TITLE_MAP = CUR_GRADE_TITLES;

// ================================================================
// محاسبه‌ی درصد پیشرفت واقعی یک کتاب برای یک کاربر
// کاملاً هماهنگ با فرمول StudentHome
// ================================================================
// 🔽 CHANGED: helper — پیدا کردن کلید COURSE_DETAILS با پشتیبانی از field
const findCourseDetailKey = (grade, subject, field = null) => {
  if (!COURSE_DETAILS || !grade || !subject) return null;

  // تلاش ۱: با field
  if (field) {
    const keyWithField = `${grade}-${field}-${subject}`;
    if (COURSE_DETAILS[keyWithField]) return keyWithField;
  }

  // تلاش ۲: بدون field
  const keyWithoutField = `${grade}-${subject}`;
  if (COURSE_DETAILS[keyWithoutField]) return keyWithoutField;

  // تلاش ۳: هر کلیدی که با grade- شروع بشه و subject در انتها داشته باشه
  const candidates = Object.keys(COURSE_DETAILS).filter((k) => {
    if (!k.startsWith(`${grade}-`)) return false;
    const parts = k.split('-');
    return parts[parts.length - 1] === subject;
  });
  if (candidates.length > 0) {
    // اگه رشته کاربر داده شده، اون رو ترجیح بده
    if (field) {
      const match = candidates.find((k) => k.includes(`-${field}-`));
      if (match) return match;
    }
    return candidates[0];
  }
  return null;
};






// ═══════════════════════════════════════════════════════════════
// 🆕 Helper: خواندن اطلاعات کتاب از dataEngine
// ═══════════════════════════════════════════════════════════════
const BOOK_TOTAL_CACHE_PREFIX = 'book-total-lessons-v2-';
const BOOK_TOTAL_TTL_MS = 24 * 60 * 60 * 1000;

const getBookLessonInfo = (grade, subject, field) => {
  const cacheKey = `${BOOK_TOTAL_CACHE_PREFIX}${grade}-${field || 'none'}-${subject}`;
  try {
    // 🆕 از engine
    const parsed = engine.get(cacheKey, null);
    if (parsed && Date.now() - (parsed.at || 0) < BOOK_TOTAL_TTL_MS) {
      return { count: parsed.count || 0, lessonIds: new Set(parsed.lessonIds || []) };
    }
  } catch {}

  let lessonIds = new Set();
  try {
    const teachers = dataEngine.query.getTeachersForSubject(grade, subject, field || null);
    for (const t of teachers) {
      try {
        const bd = dataEngine.query.getBookData(grade, subject, t.folderName, field || null);
        for (const ch of bd?.chapters || []) {
          for (const ls of ch.lessons || []) {
            const lid = ls.legacyLessonId;
            if (lid) lessonIds.add(lid);
          }
        }
      } catch {}
    }
  } catch {}

  let count = lessonIds.size;
  if (count === 0) {
    const detailKey = findCourseDetailKey(grade, subject, field);
    count = Number(detailKey ? COURSE_DETAILS?.[detailKey]?.lessons : 0) || 0;
  }

  try {
    // 🆕 از engine (LOCAL_ONLY، پس به سرور push نمی‌شه)
    engine.set(cacheKey, {
      count, lessonIds: Array.from(lessonIds), at: Date.now(),
    });
  } catch {}

  return { count, lessonIds };
};

// 🆕 Helper: خواندن quiz progress
const readQuizProgress = (userId, projectId) => {
  if (!userId || !projectId) return {};
  try {
    const key = `step-quiz-progress-${userId}-${projectId}`;
    // 🆕 از engine
    return engine.get(key, {}) || {};
  } catch { return {}; }
};

// 🆕 Helper: ساخت ایندکس progress همه‌ی درس‌ها (یک بار برای همه)
const buildProgressIndex = (userId) => {
  const index = {};
  const prefix = `lesson-progress-${userId}-`;
  try {
    // 🆕 استفاده از engine.keys به‌جای loop روی localStorage
    const keys = engine.keys(prefix);
    for (const k of keys) {
      try {
        const p = engine.get(k, {}) || {};
        const ids =
          Array.isArray(p.completedStepIds) && p.completedStepIds.length > 0
            ? p.completedStepIds
            : Array.isArray(p.completedSteps)
              ? p.completedSteps
              : [];
        index[k] = new Set(ids);
      } catch {}
    }
  } catch {}
  return index;
};

// 🆕 Helper: محاسبه‌ی نسبت یک درس (شامل آزمون‌ها)
const computeLessonRatio = (userId, rec, progressIndex) => {
  const { grade, subject, folderName, lessonId, totalSteps, projectId, quizStepIds } = rec;
  if (!totalSteps || totalSteps <= 0) return 0;

  const pKey = `lesson-progress-${userId}-${grade}-${subject}-${folderName}-${lessonId}`;
  const completedSet = progressIndex[pKey] || new Set();
  const lessonStepsDone = completedSet.size;

  let quizPassed = 0;
  if (projectId && Array.isArray(quizStepIds) && quizStepIds.length > 0) {
    const quizData = readQuizProgress(userId, projectId);
    quizPassed = quizStepIds.filter((sid) => quizData[sid]?.passed === true).length;
  }

  const completed = Math.min(totalSteps, lessonStepsDone + quizPassed);
  return completed / totalSteps;
};







// ═══════════════════════════════════════════════════════════════
// محاسبه‌ی درصد پیشرفت واقعی یک کتاب برای یک کاربر
// ═══════════════════════════════════════════════════════════════
const computeBookProgress = (userId, grade, subject, field = null) => {
  if (!userId || userId === 'unknown' || !grade || !subject) return 0;

  // ۱. رکوردهای درسی کاربر
  let records = {};
  try {
    // 🆕 از engine
    records = engine.get(`lesson-records-${userId}`, {}) || {};
  } catch {}

  // ۲. ایندکس progress (یک بار)
  const progressIndex = buildProgressIndex(userId);

  // ۳. فیلتر رکوردها
  const matching = Object.values(records).filter((rec) => {
    if (!rec) return false;
    if (curriculumNormalizeGrade(rec.grade) !== String(grade)) return false;
    if (rec.subject !== subject) return false;
    if (field && rec.field && rec.field !== field) return false;
    return true;
  });

  // ۴. بهترین نسبت برای هر lessonId
  const lessonBest = {};
  for (const rec of matching) {
    if (!rec.lessonId) continue;
    const ratio = computeLessonRatio(userId, rec, progressIndex);
    if (!lessonBest[rec.lessonId] || lessonBest[rec.lessonId] < ratio) {
      lessonBest[rec.lessonId] = ratio;
    }
  }

  // ۵. totalLessons هوشمند
  const { count: totalLessons } = getBookLessonInfo(grade, subject, field);

  // Fallback: اگه نتونستیم totalLessons رو بگیریم
  if (totalLessons <= 0) {
    const seen = Object.keys(lessonBest).length;
    if (seen > 0) {
      const sum = Object.values(lessonBest).reduce((s, r) => s + r, 0);
      return Math.min(100, Math.round((sum / seen) * 100));
    }
    return 0;
  }

  const sumRatios = Object.values(lessonBest).reduce((s, r) => s + r, 0);
  return Math.min(100, Math.round((sumRatios / totalLessons) * 100));
};

// دسته‌هایی که بر اساس پایه فیلتر می‌شن
const GRADE_AWARE_CATEGORIES = ['ai-learning', 'textbook-pdf', 'gama', 'workbook-pdf'];

// ==================== دسته‌بندی‌ها ====================
const BOOK_CATEGORIES = [
  {
    id: "ai-learning",
    label: "درس‌ها",
    icon: "🧙‍♂️",
    color: "#6C5CE7",
    emoji: "📚"
  },
  {
    id: "non-school",
    label: "مطالعه آزاد",
    icon: "🧑‍🎨",
    color: "#05c69f",
    emoji: "🎨"
  },
  {
    id: "workbook-pdf",
    label: "کتاب کار",
    icon: "🧪",
    color: "#fdea6e",
    emoji: "✏️"
  },
  {
    id: "textbook-pdf",
    label: "کتاب درسی",
    icon: "📡",
    color: "#0984E3",
    emoji: "📖"
  },
  {
    id: "gama",
    label: "جزوه و نمونه سوال",
    icon: "🧿",
    color: "#A29BFE",
    emoji: "✨"
  },
];

// ================================================================
// 🔽 CHANGED: LEARNING_SECTIONS به‌صورت خودکار از curriculum
// ----------------------------------------------------------------
// • برای ۱-۹: یک بخش به ازای هر پایه
// • برای ۱۰-۱۲: یک بخش به ازای هر (پایه + رشته)
// ================================================================

// helper: تصویر کتاب — با الگوی مشخص
const getBookImage = (subject, grade, field) => {
  // الگوی تصویر: /Books/<subject>-<grade>.jpg
  // اگه کاربر تصاویر رو جایگزین کنه، درست کار می‌کنه
  const num = grade;
  return `/Books/${subject}-${num}.jpg`;
};

const LEARNING_SECTIONS = (() => {
  const sections = [];
  let sectionId = 1;

  GRADE_LIST.forEach((g) => {
    const needsF = curriculumNeedsField(g.id);

    if (!needsF) {
      // ۱-۹: یک بخش ساده
      const items = getSubjectsFor(g.id).map((s, idx) => ({
        id: idx + 1,
        title: s.title,
        image: getBookImage(s.id, g.id, null),
        duration: '۲۰ دقیقه',
        studentsCount: 120,
        progress: 0,
        link: `/course/${g.id}/${s.id}`,
      }));
      if (items.length > 0) {
        sections.push({ id: sectionId++, title: g.title, items });
      }
    } else {
      // ۱۰-۱۲: یک بخش به ازای هر رشته
      FIELD_LIST.forEach((f) => {
        const items = getSubjectsFor(g.id, f.id).map((s, idx) => ({
          id: idx + 1,
          title: s.title,
          image: getBookImage(s.id, g.id, f.id),
          duration: '۲۰ دقیقه',
          studentsCount: 120,
          progress: 0,
          // 🔽 لینک با field
          link: `/course/${g.id}/${f.id}/${s.id}`,
          _field: f.id,
          _fieldTitle: f.shortTitle,
        }));
        if (items.length > 0) {
          sections.push({
            id: sectionId++,
            title: `${g.title} — ${f.shortTitle}`,
            _grade: g.id,
            _field: f.id,
            items,
          });
        }
      });
    }
  });

  return sections;
})();

// ==================== غیر درسی ====================
// ================================================================
// 🎨 تبدیل خودکار گروههای freeStudyData → ساختار sections
// ================================================================
const NON_SCHOOL_SECTIONS = FREE_STUDY_GROUPS.map((group) => ({
  id: group.id,
  title: group.title,
  items: group.courses.map((course) => {
    const videos = Array.isArray(course.videos) ? course.videos : [];
    return {
      id: course.id,
      title: course.title,
      image: course.image,
      duration: formatTotalDuration(videos),
      studentsCount: 124 + videos.length * 37,
      progress: 0,
      link: `/free-study/${course.id}`,
      isFreeStudy: true,
      courseId: course.id,
      totalVideos: videos.length,
    };
  }),
}));

// ==================== پی‌دی‌اف کتاب کار ====================
// ==================== کتاب کار (تولید خودکار از curriculum) ====================
const WORKBOOK_PDF_SECTIONS = (() => {
  const sections = [];
  let sectionId = 1;

  GRADE_LIST.forEach((g) => {
    const needsF = curriculumNeedsField(g.id);

    if (!needsF) {
      // ── پایه‌های ۱ تا ۹: یک بخش ساده ──
      const items = getSubjectsFor(g.id).map((s) => ({
        id: `${s.id}-${g.id}`,
        title: s.title,
        image: `/ketab-darsi/${s.id}-${g.id}.jpg`,
        duration: '۲۰ دقیقه',
        studentsCount: 120,
        progress: 0,
        link: null, 
        _staticId: `static-workbook-pdf-${g.id}-none-${s.id}`, // 🆕
      }));
      if (items.length > 0) {
        sections.push({ id: sectionId++, title: g.title, items });
      }
    } else {
      // ── پایه‌های ۱۰ تا ۱۲: یک بخش به‌ازای هر رشته ──
      FIELD_LIST.forEach((f) => {
        const items = getSubjectsFor(g.id, f.id).map((s) => ({
          id: `${s.id}-${g.id}-${f.id}`,
          title: s.title,
          image: `/ketab-darsi/${s.id}-${g.id}-${f.id}.jpg`,
          duration: '۲۰ دقیقه',
          studentsCount: 120,
          progress: 0,
          link: null,
          _field: f.id,
          _fieldTitle: f.shortTitle,
          _staticId: `static-workbook-pdf-${g.id}-${f.id}-${s.id}`, // 🆕
        }));
        if (items.length > 0) {
          sections.push({
            id: sectionId++,
            title: `${g.title} — ${f.shortTitle}`,
            _grade: g.id,
            _field: f.id,
            items,
          });
        }
      });
    }
  });

  return sections;
})();

// ==================== پی‌دی‌اف کتاب درسی (تولید خودکار از curriculum) ====================
const TEXTBOOK_PDF_SECTIONS = (() => {
  const sections = [];
  let sectionId = 1;
  
  // 🆕 تعریف آدرس سرور فایل (در حالت لوکال پورت 3002 و در سرور دامنه اصلی)
  const FILE_BASE = process.env.REACT_APP_FILE_API_URL || 'http://localhost:3002';

  GRADE_LIST.forEach((g) => {
    const needsF = curriculumNeedsField(g.id);

    if (!needsF) {
      // پایه‌های ۱-۹
      const items = getSubjectsFor(g.id).map((s, idx) => ({
        id: idx + 1,
        title: s.title,
        image: `/ketab-darsi/${s.id}-${g.id}.jpg`,
        duration: '۲۰ دقیقه',
        studentsCount: 120,
        progress: 0,
        link: `${FILE_BASE}/media/ketab-darsi/pdf/${s.id}-${g.id}.pdf`, 
        _staticId: `static-textbook-pdf-${g.id}-none-${s.id}`, // 🆕
      }));
      if (items.length > 0) {
        sections.push({ id: sectionId++, title: g.title, items });
      }
    } else {
      // پایه‌های ۱۰-۱۲ — بر اساس رشته
      FIELD_LIST.forEach((f) => {
        const items = getSubjectsFor(g.id, f.id).map((s, idx) => ({
          id: idx + 1,
          title: getSubjectTitle(g.id, s.id),
          image: `/ketab-darsi/${s.id}-${g.id}.jpg`,
          duration: '۲۰ دقیقه',
          studentsCount: 120,
          progress: 0,
          link: `${FILE_BASE}/media/ketab-darsi/pdf/${g.id}/${f.id}/${s.id}.pdf`, 
          _field: f.id,
          _fieldTitle: f.shortTitle,
          _staticId: `static-textbook-pdf-${g.id}-${f.id}-${s.id}`, // 🆕
        }));
        if (items.length > 0) {
          sections.push({
            id: sectionId++,
            title: `${g.title} — ${f.shortTitle}`,
            _grade: g.id,
            _field: f.id,
            items,
          });
        }
      });
    }
  });

  return sections;
})();

// ==================== جزوه درسی (تولید خودکار از curriculum) ====================
const GAMA_SECTIONS = (() => {
  const sections = [];
  let sectionId = 1;

  GRADE_LIST.forEach((g) => {
    const needsF = curriculumNeedsField(g.id);

    if (!needsF) {
      // پایه‌های ۱-۹
      const items = getSubjectsFor(g.id).map((s, idx) => ({
        id: idx + 1,
        title: s.title,
        image: '',
        duration: '۲۰ دقیقه',
        studentsCount: 120,
        progress: 0,
        link: `/jozwe?grade=${g.id}&subject=${s.id}`,
      }));
      if (items.length > 0) {
        sections.push({ id: sectionId++, title: g.title, items });
      }
    } else {
      // پایه‌های ۱۰-۱۲ — بر اساس رشته
      FIELD_LIST.forEach((f) => {
        const items = getSubjectsFor(g.id, f.id).map((s, idx) => ({
          id: idx + 1,
          title: getSubjectTitle(g.id, s.id),
          image: '',
          duration: '۲۰ دقیقه',
          studentsCount: 120,
          progress: 0,
          link: `/jozwe?grade=${g.id}&field=${f.id}&subject=${s.id}`,
          _field: f.id,
          _fieldTitle: f.shortTitle,
        }));
        if (items.length > 0) {
          sections.push({
            id: sectionId++,
            title: `${g.title} — ${f.shortTitle}`,
            _grade: g.id,
            _field: f.id,
            items,
          });
        }
      });
    }
  });

  return sections;
})();

// ==================== اسلایدهای بالای صفحه ====================
const BOOKS_SLIDES = [
  { id: 0, title: "یاد بگیر و آموزش بده", subtitle: "از همین‌جا شروع کن", image: "/images/book-1.jpg" },
  { id: 1, title: "آموزش های هدفمند", subtitle: "فصل به فصل جلو برو", image: "/images/book-2.jpg" },
  { id: 2, title: "ببین , بخون و آزمون بده", subtitle: "یادگیری هوشمند", image: "/images/book-3.jpg" },
];

// ==================== توابع کمکی ====================
const getItemKey = (item) => `${item.title}_${item.link || item.id}`;
const getAllItemsFromCategory = (sections) => sections.flatMap(s => s.items);





// ✅ اسلایدر جدا — state داخلی، هیچ تأثیری روی والد نداره
function BooksSlider() {
  const [activeSlide, setActiveSlide] = useState(0);

  useEffect(() => {
    const timer = setInterval(
      () => setActiveSlide(prev => (prev + 1) % BOOKS_SLIDES.length),
      5000
    );
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="slider-modern">
      <div className="slider-container">
        {BOOKS_SLIDES.map((s, i) => (
          <div
            key={s.id}
            className={`slide ${i === activeSlide ? 'active' : ''}`}
            style={{ backgroundImage: `url(${s.image})` }}
          >
            <div className="overlay"></div>
            <div className="text">
              <h2>{s.title}</h2>
              <p>{s.subtitle}</p>
            </div>
          </div>
        ))}
      </div>
      <div className="dots">
        {BOOKS_SLIDES.map((_, i) => (
          <button
            key={i}
            className={`dot ${i === activeSlide ? 'active' : ''}`}
            onClick={() => setActiveSlide(i)}
          />
        ))}
      </div>
    </div>
  );
}







export default function BooksPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const userGrade = normalizeGrade(user?.grade);

  // 🔽 CHANGED: رشته کاربر برای پایه‌های ۱۰-۱۲
const userField = useMemo(
  () => (user?.field ? curriculumNormalizeField(user.field) : null),
  [user?.field]
);














  // ===== ✅ State تم تاریک/روشن =====
const [isDark, setIsDark] = useState(() => {
  try {
    return engine.getRaw('navbar2-drawer-dark') === '1';
  } catch {
    return false;
  }
});

  // ===== ✅ اعمال تم روی صفحه =====
// ذخیره در کلید مشترک Navbar2 + اعمال data-theme روی html
useEffect(() => {
  try {
    // 🆕 از engine → remoteSync → userPrefs (سرور)
    engine.setRaw('navbar2-drawer-dark', isDark ? '1' : '0');
    document.documentElement.setAttribute('data-theme', isDark ? 'dark' : '');
  } catch {}
  return () => {
    document.documentElement.setAttribute('data-theme', '');
  };
}, [isDark]);

// گوش دادن به تغییرات Navbar2 (storage بین تب‌ها + polling داخل همون تب)
useEffect(() => {
  const handleStorage = (e) => {
    if (e.key === 'navbar2-drawer-dark') {
      setIsDark(e.newValue === '1');
    }
  };
  window.addEventListener('storage', handleStorage);

  const interval = setInterval(() => {
    try {
      // 🆕 از engine
      const current = engine.getRaw('navbar2-drawer-dark') === '1';
      setIsDark((prev) => (prev !== current ? current : prev));
    } catch {}
  }, 500);

  return () => {
    window.removeEventListener('storage', handleStorage);
    clearInterval(interval);
  };
}, []);

  const [activeCategory, setActiveCategory] = useState("ai-learning");
  const [isCategoryDrawerOpen, setIsCategoryDrawerOpen] = useState(false);
  const [expandedSectionId, setExpandedSectionId] = useState(null);
  const [showOtherGrades, setShowOtherGrades] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [progressTick, setProgressTick] = useState(0);
  const [studentCounts, setStudentCounts] = useState({});
  const [fsProgress, setFsProgress] = useState({});
  const [streak, setStreak] = useState(3);
  const [dailyMedal, setDailyMedal] = useState("🏅");
  const [todayMinutes, setTodayMinutes] = useState(0);
  const [username] = useState("سارا");
  const [isListening, setIsListening] = useState(false);
  const searchInputRef = useRef(null);

  const [purchaseModal, setPurchaseModal] = useState({ open: false, book: null, isRenewal: false });
  const [downloadModal, setDownloadModal] = useState({ open: false, book: null });
  const [licenses, setLicenses] = useState({});
const [downloadCounts, setDownloadCounts] = useState({});
const [lessonStudentCounts, setLessonStudentCounts] = useState({});
const [teacherCounts, setTeacherCounts] = useState({});
const [freeStudyStudentCounts, setFreeStudyStudentCounts] = useState({});

// 🆕 بخش‌های داینامیک مطالعه آزاد (reactive)
const [dynamicFreeStudySections, setDynamicFreeStudySections] = useState([]);
const adminUploads = useAdminUploads(); // 🆕

// 🆕 افزایش شمارنده دانلود (ذخیره‌ی واقعی در سرور)
const incrementDownload = useCallback(async (bookId) => {
  if (!bookId) return;
  const id = String(bookId);
  try {
    const checkRes = await fetch(`/api/db/bookDownloadCounts?bookId=${encodeURIComponent(id)}`);
    if (!checkRes.ok) return;
    const arr = await checkRes.json();

    if (Array.isArray(arr) && arr.length > 0) {
      const existing = arr[0];
      const newCount = (Number(existing.count) || 0) + 1;
      await fetch(`/api/db/bookDownloadCounts/${existing.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ count: newCount, updatedAt: new Date().toISOString() }),
      });
      setDownloadCounts(prev => ({ ...prev, [id]: newCount }));
    } else {
      const newId = `dl_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      await fetch(`/api/db/bookDownloadCounts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: newId,
          bookId: id,
          count: 1,
          updatedAt: new Date().toISOString(),
        }),
      });
      setDownloadCounts(prev => ({ ...prev, [id]: 1 }));
    }
  } catch (e) {
    console.warn('incrementDownload failed:', e);
  }
}, []);

// 🆕 ساخت شمارنده واقعی دانش‌آموزان یکتای هر (grade/subject/field) برای درس‌ها
useEffect(() => {
  if (activeCategory !== 'ai-learning') return;

  let cancelled = false;
  const load = async () => {
    const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';

    const studentCounts = {};
    try {
      const res = await fetch(`${API_BASE}/lessonRecords`);
      if (res.ok) {
        const list = await res.json();
        if (Array.isArray(list)) {
          const perKey = {};
          for (const rec of list) {
            const userId =
              rec._userId || String(rec.id || '').replace(/^lesson-records-/, '');
            const data = rec.data || {};
            if (!userId || !data || typeof data !== 'object') continue;
            for (const r of Object.values(data)) {
              if (!r || !r.grade || !r.subject) continue;
              const key = r.field
                ? `${r.grade}-${r.field}-${r.subject}`
                : `${r.grade}-${r.subject}`;
              if (!perKey[key]) perKey[key] = new Set();
              perKey[key].add(userId);
            }
          }
          Object.keys(perKey).forEach((k) => {
            studentCounts[k] = perKey[k].size;
          });
        }
      }
    } catch (e) {
      console.warn('lesson student count fetch failed:', e);
    }

    const teacherCnt = {};
    try {
      for (const section of LEARNING_SECTIONS) {
        for (const item of section.items) {
          const parts = (item.link || '').split('/').filter(Boolean);
          if (parts[0] !== 'course') continue;
          const g = parts[1];
          let fld = null;
          let subj = null;
          if (parts.length === 3) subj = parts[2];
          else if (parts.length === 4) {
            fld = parts[2];
            subj = parts[3];
          }
          if (!g || !subj) continue;
          try {
            teacherCnt[item.link] = dataEngine.query.getTeachersForSubject(
              g,
              subj,
              fld
            ).length;
          } catch {
            teacherCnt[item.link] = 0;
          }
        }
      }
    } catch (e) {
      console.warn('teacherCounts build failed:', e);
    }

    if (!cancelled) {
      setLessonStudentCounts(studentCounts);
      setTeacherCounts(teacherCnt);
    }
  };

  load();
  return () => {
    cancelled = true;
  };
}, [activeCategory, progressTick]);

// 🆕 شمارنده واقعی دانش‌آموزان مطالعه آزاد (progress + purchase)
useEffect(() => {
  if (activeCategory !== 'non-school') return;

  let cancelled = false;
  const load = async () => {
    const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';
    const counts = {};

    try {
      const res = await fetch(`${API_BASE}/freeStudyProgress`);
      if (res.ok) {
        const list = await res.json();
        if (Array.isArray(list)) {
          const perCourse = {};
          for (const rec of list) {
            if (!rec?.userId || !rec?.courseId) continue;
            const hasActivity =
              (Array.isArray(rec.completedVideoIds) &&
                rec.completedVideoIds.length > 0) ||
              rec.lastVideoId ||
              rec.lastVisited;
            if (!hasActivity) continue;
            if (!perCourse[rec.courseId]) perCourse[rec.courseId] = new Set();
            perCourse[rec.courseId].add(rec.userId);
          }
          Object.keys(perCourse).forEach((cid) => {
            counts[cid] = { studentCount: perCourse[cid].size };
          });
        }
      }
    } catch (e) {
      console.warn('freeStudyProgress fetch failed:', e);
    }

    try {
      const res = await fetch(`${API_BASE}/freeStudyPurchases`);
      if (res.ok) {
        const list = await res.json();
        if (Array.isArray(list)) {
          const perCourse = {};
          for (const rec of list) {
            if (!rec?.userId || !rec?.courseId) continue;
            const hasTx =
              Array.isArray(rec.transactions) && rec.transactions.length > 0;
            if (!hasTx) continue;
            if (!perCourse[rec.courseId]) perCourse[rec.courseId] = new Set();
            perCourse[rec.courseId].add(rec.userId);
          }
          Object.keys(perCourse).forEach((cid) => {
            const existing = counts[cid]?.studentCount || 0;
            counts[cid] = {
              studentCount: Math.max(existing, perCourse[cid].size),
            };
          });
        }
      }
    } catch (e) {
      console.warn('freeStudyPurchases fetch failed:', e);
    }

    if (!cancelled) setFreeStudyStudentCounts(counts);
  };

  load();
  return () => {
    cancelled = true;
  };
}, [activeCategory, progressTick]);


useEffect(() => {
  const update = () => {
    try {
      setDynamicFreeStudySections(getDynamicFreeStudySections());
    } catch (e) {
      console.warn('dynamic free study sections failed:', e);
    }
  };
  update();
  let unsub = null;
  try { unsub = engine.on('registry:project:changed', update); } catch {}
  return () => { try { unsub && unsub(); } catch {} };
}, []);

  // اضافه کردن داخل کامپوننت BooksPage (بعد از ref ها)
  useEffect(() => {
    const hero = document.querySelector('.hero-aurora');
    const glassCard = document.querySelector('.glass-layer');
    const alienIcon = document.querySelector('.alien-icon-big');
    const rainbowLine = document.querySelector('.rainbow-line-short');
    const titleElement = document.querySelector('.aurora-title-right');
    if (!hero) return;

    let ticking = false;
    const handleMouseMove = (e) => {
      if (!ticking) {
        requestAnimationFrame(() => {
          const rect = hero.getBoundingClientRect();
          const x = e.clientX - rect.left;
          const y = e.clientY - rect.top;
          const centerX = rect.width / 2;
          const centerY = rect.height / 2;
          const moveX = (x - centerX) / 25;
          const moveY = (y - centerY) / 25;
          hero.style.backgroundPosition = `${50 + moveX * 1.8}% ${50 + moveY * 1.8}%`;
          hero.style.transition = 'background-position 0.08s linear';
          if (glassCard) {
            const tiltX = (y - centerY) / 55;
            const tiltY = (x - centerX) / -55;
            glassCard.style.transform = `perspective(900px) rotateX(${tiltX}deg) rotateY(${tiltY}deg) translateZ(6px)`;
            glassCard.style.transition = 'transform 0.12s cubic-bezier(0.03, 0.98, 0.52, 0.99)';
          }
          if (alienIcon) {
            const iconShiftX = (x - centerX) / 40;
            const iconShiftY = (y - centerY) / 30;
            alienIcon.style.transform = `translate(${iconShiftX}px, ${iconShiftY}px) rotate(${iconShiftX * 0.5}deg)`;
            alienIcon.style.transition = 'transform 0.08s linear';
          }
          ticking = false;
        });
        ticking = true;
      }
    };
    const resetStyles = () => {
      if (glassCard) glassCard.style.transform = '';
      if (alienIcon) alienIcon.style.transform = '';
      hero.style.backgroundPosition = '';
      hero.style.transition = 'background-position 0.6s ease';
    };
    hero.addEventListener('mousemove', handleMouseMove);
    hero.addEventListener('mouseleave', resetStyles);

    // جرقه تصادفی روی خط رنگین‌کمان
    let interval;
    if (rainbowLine) {
      interval = setInterval(() => {
        if (Math.random() > 0.75) {
          rainbowLine.style.filter = 'drop-shadow(0 0 8px #ffbb77)';
          setTimeout(() => {
            if (rainbowLine) rainbowLine.style.filter = '';
          }, 250);
        }
      }, 1300);
    }

    // کلیک جادویی روی عنوان
    if (titleElement) {
      const clickHandler = (e) => {
        e.stopPropagation();
        titleElement.style.animation = 'none';
        titleElement.offsetHeight;
        titleElement.style.animation = 'glitchText 0.2s ease-in-out';
        setTimeout(() => {
          if (titleElement) titleElement.style.animation = '';
        }, 280);
        const bubble = document.createElement('div');
        bubble.textContent = '📖';
        bubble.style.position = 'fixed';
        bubble.style.left = `${e.clientX}px`;
        bubble.style.top = `${e.clientY}px`;
        bubble.style.fontSize = '26px';
        bubble.style.pointerEvents = 'none';
        bubble.style.opacity = '0.9';
        bubble.style.transition = 'all 0.5s ease-out';
        bubble.style.transform = 'translateY(0)';
        bubble.style.filter = 'drop-shadow(0 0 6px gold)';
        document.body.appendChild(bubble);
        setTimeout(() => {
          bubble.style.transform = 'translateY(-50px) scale(1.5)';
          bubble.style.opacity = '0';
          setTimeout(() => bubble.remove(), 600);
        }, 20);
      };
      titleElement.addEventListener('click', clickHandler);
      return () => {
        hero.removeEventListener('mousemove', handleMouseMove);
        hero.removeEventListener('mouseleave', resetStyles);
        if (interval) clearInterval(interval);
        titleElement.removeEventListener('click', clickHandler);
      };
    }
    return () => {
      hero.removeEventListener('mousemove', handleMouseMove);
      hero.removeEventListener('mouseleave', resetStyles);
      if (interval) clearInterval(interval);
    };
  }, []);

  // بارگذاری داده‌ها — با کلید per-user برای sync با سرور
  useEffect(() => {
    if (!user?.id) return;
    try {
      // 🆕 از engine
      const parsed = engine.get(`user-stats-${user.id}`, null);
      if (parsed) {
        if (typeof parsed.studyStreak === 'number') setStreak(parsed.studyStreak);
        if (typeof parsed.todayMinutes === 'number') setTodayMinutes(parsed.todayMinutes);
      }
    } catch {}
  }, [user?.id]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (todayMinutes < 120) setTodayMinutes(prev => prev + 1);
    }, 60000);
    return () => clearInterval(interval);
  }, [todayMinutes]);

  useEffect(() => {
    if (!user?.id) return;
    try {
      // 🆕 از engine → remoteSync → userStats (سرور)
      engine.set(`user-stats-${user.id}`, {
        studyStreak: streak,
        todayMinutes,
        updatedAt: Date.now(),
      });
    } catch {}
  }, [streak, todayMinutes, user?.id]);

  // جستجوی صوتی
  const startVoiceSearch = () => {
    setIsListening(true);
    setTimeout(() => {
      setIsListening(false);
      setSearchQuery("ریاضی");
      searchInputRef.current?.focus();
    }, 2000);
  };

const getSectionsByCategory = useCallback((catId) => {
  let baseSections = [];
  switch (catId) {
    case "ai-learning": baseSections = LEARNING_SECTIONS; break;
    case "non-school": baseSections = [...NON_SCHOOL_SECTIONS, ...dynamicFreeStudySections]; break;
    case "workbook-pdf": baseSections = WORKBOOK_PDF_SECTIONS; break;
    case "textbook-pdf": baseSections = TEXTBOOK_PDF_SECTIONS; break;
    case "gama": baseSections = GAMA_SECTIONS; break;
    default: baseSections = [];
  }

  if (catId === 'ai-learning' || catId === 'non-school') return baseSections;

  const deletedKeys = new Set(adminUploads.filter(u => u.isDeleted).map(u => u.bookId));

  return baseSections.map(section => {
    let sectionGrade = section._grade;
    if (!sectionGrade) {
      const g = GRADE_LIST.find(gr => gr.title === section.title);
      if (g) sectionGrade = g.id;
    }
    if (!sectionGrade) return section;

    const FILE_BASE = process.env.REACT_APP_FILE_API_URL || 'http://localhost:3002';

    // 🆕 منطق اختصاصی برای جزوه درسی (Gama)
    // ما کارت جدیدی نمی‌سازیم، فقط عکس کارت اصلی را با کاور آپلودی آپدیت می‌کنیم
    if (catId === 'gama') {
      const updatedItems = section.items.map(item => {
        // استخراج شناسه درس از لینک کارت (مثلاً /jozwe?grade=1&subject=riazi)
        const match = item.link?.match(/subject=([^&]+)/);
        const subjectId = match ? match[1] : null;

        if (!subjectId) return item;

        // پیدا کردن اولین جزوه‌ای که مدیر برای این درس آپلود کرده و کاور دارد
        const uploadedWithCover = adminUploads.find(u =>
          !u.isDeleted &&
          (u.category === 'gama' || u.category === 'exam-pdf') &&
          String(u.grade) === String(sectionGrade) &&
          u.subject === subjectId &&
          (!section._field || u.field === section._field) &&
          u.coverImage
        );

        // اگر کاوری پیدا شد، عکس کارت اصلی را تغییر بده
        return {
          ...item,
          image: uploadedWithCover ? `${FILE_BASE}${uploadedWithCover.coverImage}` : item.image
        };
      });

      return { ...section, items: updatedItems };
    }

    // 🆕 برای کتاب درسی و کتاب کار، منطق قبلی (افزودن کارت جدید) حفظ می‌شود
// 🆕 برای کتاب درسی و کتاب کار، منطق قبلی (افزودن کارت جدید) حفظ می‌شود
const filteredStaticItems = section.items.filter(item => {
  if (item._staticId && deletedKeys.has(item._staticId)) return false;
  return true;
});

const customItems = adminUploads.filter(u => {
  if (u.isDeleted) return false;
  if (u.category !== catId) return false;
  if (String(u.grade) !== String(sectionGrade)) return false;
  if (u.field && u.field !== section._field) return false;
  return true;
}).sort((a, b) => (a.sortIndex || 0) - (b.sortIndex || 0))
  .map(u => ({
  id: u.bookId || u.id,
  title: u.title,
  image: u.coverImage ? `${FILE_BASE}${u.coverImage}` : '/Books/default-book.jpg',
  duration: '۲۰ دقیقه',
  studentsCount: 120,
  progress: 0,
  link: catId === 'workbook-pdf' ? null : `${FILE_BASE}${u.fileUrl}`,
  _field: u.field,
  _fieldTitle: u.field,
  isCustom: true,
  customPricing: u.category === 'workbook-pdf' ? {
    coinPrice: (u.coinPrice !== undefined && u.coinPrice !== null && u.coinPrice !== '') ? Number(u.coinPrice) : 500,
    tomanPrice: (u.tomanPrice !== undefined && u.tomanPrice !== null && u.tomanPrice !== '') ? Number(u.tomanPrice) : 50000,
    durationDays: 365
  } : undefined,
}));

// 🆕 فقط برای کتاب کار و کتاب درسی: کارت‌های استاتیک نمایش داده نشوند (فقط فایل‌های اپلودشده)
if (catId === 'workbook-pdf' || catId === 'textbook-pdf') return { ...section, items: customItems };

if (filteredStaticItems.length === 0 && customItems.length === 0) return section;
return { ...section, items: [...filteredStaticItems, ...customItems] };
  }).filter(sec => sec.items.length > 0);
}, [dynamicFreeStudySections, adminUploads]);

  // فیلتر بر اساس پایه تحصیلی + جستجو
  const sections = useMemo(() => {
    let filtered = getSectionsByCategory(activeCategory);

    // فقط برای دسته‌هایی که بر اساس پایه هستن
    if (userGrade && GRADE_AWARE_CATEGORIES.includes(activeCategory)) {
      const targetTitle = GRADE_TITLE_MAP[userGrade];

      filtered = filtered.filter((s) => {
        // حالت ۱: برای ۱-۹، عنوان دقیقاً برابر "پایه X" هست
        if (!curriculumNeedsField(userGrade)) {
          return s.title === targetTitle;
        }

        // حالت ۲: برای ۱۰-۱۲، ساختار پایه-رشته هست
        // اگه _grade روی section باشه، چک دقیق‌تری انجام بده
        if (s._grade) {
          if (String(s._grade) !== String(userGrade)) return false;
          // اگه کاربر رشته داره، فقط همون رشته
          if (userField) return s._field === userField;
          // اگه کاربر رشته نداره، همه‌ی رشته‌های این پایه رو نشون بده
          return true;
        }

        // fallback برای ۱۰-۱۲ در legacy structure (بدون _grade)
        return s.title.startsWith(targetTitle);
      });
    }

    if (!searchQuery.trim()) return filtered;
    const q = searchQuery.trim().toLowerCase();
    return filtered
      .map(s => ({ ...s, items: s.items.filter(i => i.title.toLowerCase().includes(q)) }))
      .filter(s => s.items.length);
  }, [activeCategory, searchQuery, getSectionsByCategory, userGrade, userField]);

  const otherGradeSections = useMemo(() => {
    if (!userGrade || !GRADE_AWARE_CATEGORIES.includes(activeCategory)) return [];

    const targetTitle = GRADE_TITLE_MAP[userGrade];
    const needsF = curriculumNeedsField(userGrade);

    let filtered = getSectionsByCategory(activeCategory).filter((s) => {
      // آیا این بخش، بخش پایه‌ی کاربر است؟ (همان شرط `sections`)
      const isUserSection = !needsF
        ? s.title === targetTitle
        : s._grade
          ? (String(s._grade) === String(userGrade) && (!userField || s._field === userField))
          : s.title.startsWith(targetTitle);

      return !isUserSection;
    });

    if (!searchQuery.trim()) return filtered;
    const q = searchQuery.trim().toLowerCase();
    return filtered
      .map(s => ({ ...s, items: s.items.filter(i => i.title.toLowerCase().includes(q)) }))
      .filter(s => s.items.length);
  }, [activeCategory, searchQuery, getSectionsByCategory, userGrade, userField]);

  // محاسبه‌ی درصد واقعی پیشرفت هر کتاب
  const realProgressMap = useMemo(() => {
    if (!user?.id || user.id === 'unknown' || !userGrade) return {};
    const map = {};

    // 🔽 CHANGED: پشتیبانی از کلیدهای «grade-subject» و «grade-field-subject»
    Object.keys(COURSE_DETAILS || {}).forEach((key) => {
      if (!key.startsWith(`${userGrade}-`)) return;

      const parts = key.split('-');
      // parts[0] = grade
      // اگه ۲ بخش داریم: parts[1] = subject (پایه‌های ۱-۹)
      // اگه ۳ بخش داریم: parts[1] = field، parts[2] = subject (پایه‌های ۱۰-۱۲)
      let subject, field = null;

      if (parts.length === 2) {
        subject = parts[1];
      } else if (parts.length >= 3) {
        field = parts[parts.length - 2];
        subject = parts[parts.length - 1];
      } else {
        return;
      }

      if (!subject) return;

      // کلید نقشه: با field اگه موجود باشه
      const mapKey = field ? `${userGrade}-${field}-${subject}` : `${userGrade}-${subject}`;
      map[mapKey] = computeBookProgress(user.id, userGrade, subject, field);
    });

    return map;
  }, [user?.id, userGrade, activeCategory, userField, progressTick]);  // 🆕 progressTick

  useEffect(() => {
    const all = getAllItemsFromCategory(getSectionsByCategory(activeCategory));
    const counts = {};
    all.forEach(item => { counts[getItemKey(item)] = (item.studentsCount || 0) + Math.floor(Math.random() * 51); });
    setStudentCounts(counts);
  }, [activeCategory, getSectionsByCategory]);

const handleViewLesson = useCallback(async (item) => {
  if (activeCategory === 'workbook-pdf') {
    // ۱. از engine چک کن (کش سریع، همگام با سرور)
    if (user?.id) {
      try {
        // 🆕 از engine
        const lic = engine.get(`workbook-license-${user.id}-${item.id}`, null);
        if (lic && lic.expiresAt && new Date(lic.expiresAt) > new Date()) {
          navigate(`/workbook/${item.id}`);
          return;
        }
      } catch {}
    }

    // ۲. از state
    let license = licenses[item.id];

    // ۳. از سرور
    if (!license && user?.id) {
      try {
        license = await workbookService.checkLicense(user.id, item.id);
      } catch (e) {
        license = null;
      }
    }

    // ۴. اگر معتبر → PDF
if (license && !license.expired) {
  if (new Date(license.expiresAt) > new Date()) {
    // 🆕 ثبت دانلود قبل از باز کردن
    incrementDownload(String(item._staticId || item.id || ''));
    navigate(`/workbook/${item.id}`);
    return;
  }
}

    // ۵. در غیر این صورت → مودال
    setPurchaseModal({
      open: true,
      book: item,
      isRenewal: !!(license && license.expired)
    });
    return;
  }

    if (activeCategory === 'textbook-pdf') {
    setDownloadModal({ open: true, book: item });
    return;
  }

  // بقیه بدون تغییر
  if (!item.link) return;
  const isPdf = item.link.endsWith('.pdf');
  const isExternal = item.link.startsWith('http') || item.link.startsWith('/media/') || item.link.startsWith('/ketab-darsi/');

  if (isPdf || isExternal) {
    // 🔽 روش مطمئن برای دانلود/باز کردن فایل بدون بلاک شدن توسط مرورگر
    const a = document.createElement('a');
    a.href = item.link;
    a.target = '_blank'; // باز شدن در تب جدید
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  } else {
    navigate(item.link);
  }
}, [navigate, activeCategory, licenses, user?.id, incrementDownload]);

  const handleCategorySelect = (id) => {
    setActiveCategory(id);
    setIsCategoryDrawerOpen(false);
    setSearchQuery("");
  };

    // 🆕 تابع قدرتمند دانلود فایل (سازگار با مرورگر و APK)
  const handleConfirmDownload = async () => {
    const item = downloadModal.book;
    setDownloadModal({ open: false, book: null }); // بستن مودال

    if (!item || !item.link) return;

    const url = item.link;
    // استخراج نام فایل از لینک برای ذخیره در گوشی
    const filename = url.split('/').pop() || `${item.title}.pdf`;

    try {
      // روش Blob: این روش باعث می‌شود WebView گوشی فایل را به عنوان دانلود بشناسد
      const response = await fetch(url);
      if (!response.ok) throw new Error('Network response was not ok');
      
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename; // این خط به گوشی می‌گوید فایل را دانلود کن
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      
      // آزادسازی حافظه
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);

      incrementDownload(String(item._staticId || item.id || ''));
      
    } catch (error) {
      console.error("خطا در دانلود:", error);
      // روش جایگزین در صورت خطای شبکه
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.target = '_blank';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  };

  {/*useEffect(() => {
    const timer = setInterval(() => setActiveSlide(prev => (prev + 1) % BOOKS_SLIDES.length), 5000);
    return () => clearInterval(timer);
  }, []);*/}

// 🆕 رفرش خودکار پیشرفت (same-tab + cross-tab + focus + visibility)
useEffect(() => {
  const bump = () => setProgressTick((t) => t + 1);
  const invalidateCache = () => {
    try {
      // 🆕 استفاده از engine.keys و engine.remove
      const keys = engine.keys('book-total-lessons-v2-');
      keys.forEach((k) => engine.remove(k));
    } catch {}
    setProgressTick((t) => t + 1);
  };

  const unsubs = [];
  try { unsubs.push(engine.on('progress:changed', bump)); } catch {}
  try { unsubs.push(engine.on('quiz:step-progress-changed', bump)); } catch {}
  try { unsubs.push(engine.on('quiz:step-passed', bump)); } catch {}
  try { unsubs.push(engine.on('quiz:unlocked', bump)); } catch {}
  try { unsubs.push(engine.on('registry:project:changed', invalidateCache)); } catch {}
  try { unsubs.push(engine.on('registry:version:changed', invalidateCache)); } catch {}

  const onStorage = (e) => {
    if (!e.key) return;
    if (
      e.key.startsWith('lesson-progress-') ||
      e.key.startsWith('lesson-records-') ||
      e.key.startsWith('step-quiz-progress-') ||
      e.key.startsWith('freestudy-progress-')
    ) {
      bump();
    }
  };
  window.addEventListener('storage', onStorage);

  const onFocus = () => bump();
  const onVisibility = () => { if (!document.hidden) bump(); };
  window.addEventListener('focus', onFocus);
  document.addEventListener('visibilitychange', onVisibility);

  return () => {
    unsubs.forEach((u) => { try { u(); } catch {} });
    window.removeEventListener('storage', onStorage);
    window.removeEventListener('focus', onFocus);
    document.removeEventListener('visibilitychange', onVisibility);
  };
}, []);

  // 🆕 دریافت پیشرفت مطالعه آزاد از سرور (همگام با صفحه FreeStudyCourse)
  useEffect(() => {
    if (!user?.id || activeCategory !== 'non-school') return;
    
    let isMounted = true;
    const fetchFsProgress = async () => {
      try {
        const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';
        const res = await fetch(`${API_BASE}/freeStudyProgress?userId=${encodeURIComponent(user.id)}`);
        const data = await res.json();
        if (!isMounted) return;
        
        const map = {};
        if (Array.isArray(data)) {
          data.forEach(rec => {
            map[rec.courseId] = (rec.completedVideoIds || []).length;
          });
        }
        setFsProgress(map);
      } catch (e) {
        console.warn('Failed to fetch free study progress:', e);
      }
    };
    fetchFsProgress();
    
    return () => { isMounted = false; };
  }, [user?.id, activeCategory, progressTick]);





// 🆕 بارگذاری شمارنده‌های دانلود کتاب کار و کتاب درسی
useEffect(() => {
  if (activeCategory !== 'workbook-pdf' && activeCategory !== 'textbook-pdf') return;

  let cancelled = false;
  const load = async () => {
    try {
      const res = await fetch('/api/db/bookDownloadCounts');
      if (!res.ok) return;
      const data = await res.json();
      if (cancelled) return;

      const map = {};
      if (Array.isArray(data)) {
        data.forEach(rec => {
          const bid = String(rec.bookId || '');
          if (bid) map[bid] = Number(rec.count) || 0;
        });
      }
      setDownloadCounts(map);
    } catch (e) {
      console.warn('load download counts failed:', e);
    }
  };

  load();

  const onVis = () => { if (!document.hidden) load(); };
  document.addEventListener('visibilitychange', onVis);
  window.addEventListener('focus', load);

  return () => {
    cancelled = true;
    document.removeEventListener('visibilitychange', onVis);
    window.removeEventListener('focus', load);
  };
}, [activeCategory]);





  // بارگذاری لایسنس‌ها — روی هر تغییر مسیر یا focus دوباره اجرا می‌شود
useEffect(() => {
  if (!user?.id) return;

    // بارگذاری لایسنس‌ها — روی هر تغییر مسیر یا focus دوباره اجرا می‌شود


  const loadLicenses = () => {
    workbookService.getUserLicenses(user.id).then(list => {
      const map = {};
      list.forEach(l => { map[l.bookId] = l; });
      setLicenses(map);
    }).catch(() => {});
  };

  loadLicenses();

  window.addEventListener('focus', loadLicenses);

  // 🆕 همگام‌سازی بین تب‌ها: اگر تب دیگری لایسنس فعال کرد
  const onStorage = (e) => {
    if (!e.key) return;
    if (e.key.startsWith(`workbook-license-${user.id}-`)) {
      loadLicenses();
    }
  };
  window.addEventListener('storage', onStorage);

  return () => {
    window.removeEventListener('focus', loadLicenses);
    window.removeEventListener('storage', onStorage);
  };
}, [user?.id, location.pathname]);

const BookCard = ({ item, isLarge = false }) => {
  const key = getItemKey(item);

  // 🆕 تشخیص کتاب کار
  const isWorkbook = activeCategory === 'workbook-pdf';

  // 🆕 وضعیت لایسنس
  const license = isWorkbook ? licenses[item.id] : null;
  const hasValidLicense = !!(
    license &&
    !license.expired &&
    license.expiresAt &&
    new Date(license.expiresAt) > new Date()
  );

  // 🆕 قیمت اختصاصی این کتاب کار (با پشتیبانی از قیمت‌های ادمین)
  const pricing = isWorkbook ? (item.customPricing || getWorkbookPricing(item.id)) : null;

  // ── محاسبه پیشرفت ──
  let prog;
  if (item.isFreeStudy === true) {
    // 🆕 استفاده از پیشرفت سرور (که در useEffect بالا دریافت شد)
    const serverCount = fsProgress[item.courseId];
    if (serverCount !== undefined && item.totalVideos > 0) {
      prog = Math.min(100, Math.round((serverCount / item.totalVideos) * 100));
    } else {
      prog = computeFreeStudyProgress(user?.id, item.courseId, item.totalVideos);
    }
  } else {
    const subject = item.link?.split('/').pop();
    let realProg;
    if (subject && userGrade) {
      if (curriculumNeedsField(userGrade) && item._field) {
        realProg = realProgressMap[`${userGrade}-${item._field}-${subject}`];
        if (realProg === undefined) {
          realProg = realProgressMap[`${userGrade}-${subject}`];
        }
      } else {
        realProg = realProgressMap[`${userGrade}-${subject}`];
      }
    }
    prog = realProg !== undefined ? realProg : (item.progress || 0);
  }

  const students = studentCounts[key] || item.studentsCount || 0;

  const R = 18;
  const CIRC = 2 * Math.PI * R;
  const safeProg = Math.max(0, Math.min(100, Number(prog) || 0));
  const dashOffset = CIRC * (1 - safeProg / 100);

  // 🆕 برچسب دکمه
  const buttonLabel =
  activeCategory === 'textbook-pdf'
    ? 'دانلود'
    : activeCategory === 'gama'
      ? 'مشاهده'
      : isWorkbook && !hasValidLicense
        ? 'خرید'
        : (safeProg > 0 ? (safeProg === 100 ? 'مرور' : 'ادامه') : 'شروع');

  // 🆕 کلاس وضعیت
const statusClass = isWorkbook
  ? (hasValidLicense ? 'is-unlocked' : 'is-locked')
  : '';

// 🆕 تشخیص دسته
const isGama = activeCategory === 'gama';
const isTextbook = activeCategory === 'textbook-pdf';
const isWorkbookCat = activeCategory === 'workbook-pdf';
const isAiLearning = activeCategory === 'ai-learning';
const isNonSchool = activeCategory === 'non-school';

// ─── جزوه / کتاب ───
let gamaCount = 0;
if (isGama) {
  const gradeMatch = item.link?.match(/grade=([^&]+)/);
  const fieldMatch = item.link?.match(/field=([^&]+)/);
  const subjectMatch = item.link?.match(/subject=([^&]+)/);
  const gGrade = gradeMatch ? decodeURIComponent(gradeMatch[1]) : null;
  const gField = fieldMatch ? decodeURIComponent(fieldMatch[1]) : null;
  const gSubject = subjectMatch ? decodeURIComponent(subjectMatch[1]) : null;
  if (gGrade && gSubject) {
    gamaCount = (adminUploads || []).filter(
      (u) =>
        !u.isDeleted &&
        (u.category === 'gama' || u.category === 'exam-pdf') &&
        String(u.grade) === String(gGrade) &&
        u.subject === gSubject &&
        (!gField || u.field === gField)
    ).length;
  }
}

const bookStatsId = String(item._staticId || item.id || '');
const dlCount = isTextbook || isWorkbookCat
  ? Number(downloadCounts[bookStatsId]) || 0
  : 0;

// ─── درس‌ها (ai-learning): معلم + دانش‌آموز ───
let teacherCount = 0;
let lessonStudentCount = 0;
if (isAiLearning) {
  teacherCount = teacherCounts[item.link] || 0;
  const parts = (item.link || '').split('/').filter(Boolean);
  if (parts[0] === 'course') {
    const g = parts[1];
    let fld = null;
    let subj = null;
    if (parts.length === 3) subj = parts[2];
    else if (parts.length === 4) {
      fld = parts[2];
      subj = parts[3];
    }
    if (g && subj) {
      const countKey = fld ? `${g}-${fld}-${subj}` : `${g}-${subj}`;
      lessonStudentCount = lessonStudentCounts[countKey] || 0;
    }
  }
}

// ─── مطالعه آزاد: دانش‌آموزان واقعی ───
let fsStudentCount = 0;
if (isNonSchool) {
  const cid = item.courseId || item.id;
  fsStudentCount = freeStudyStudentCounts[cid]?.studentCount || 0;
}

// ─── تصمیم نمایش ───
const showSingleMeta = isGama || isTextbook || isWorkbookCat;
const showAiMeta = isAiLearning;
const showFsMeta = isNonSchool;
const showLegacyMeta = !showSingleMeta && !showAiMeta && !showFsMeta;




  return (
    <div className={`book-card ${isLarge ? 'large' : ''} ${statusClass}`}>
      <div className="book-card-image-wrapper">
        {item.image
          ? <img src={item.image} alt={item.title} />
          : <div className="placeholder">📘</div>}

        {/* 🆕 بج قفل */}
        {isWorkbook && !hasValidLicense && (
          <div className="wb-lock-badge" title="خریداری نشده">
            <span>🔒</span>
          </div>
        )}

        {/* 🆕 بج باز */}
        {isWorkbook && hasValidLicense && (
          <div className="wb-unlock-badge" title="خریداری شده">
            <span>✓</span>
          </div>
        )}
      </div>

{showSingleMeta && (
  <div className="book-card-meta single">
    {isGama && (
      <span>📚 {gamaCount.toLocaleString('fa-IR')} جزوه / نمونه سوال</span>
    )}
    {(isTextbook || isWorkbookCat) && (
      <span>📥 {dlCount.toLocaleString('fa-IR')} دانلود</span>
    )}
  </div>
)}

{showAiMeta && (
  <div className="book-card-meta">
    <span>🎓 {teacherCount.toLocaleString('fa-IR')} معلم</span>
    <span>👥 {lessonStudentCount.toLocaleString('fa-IR')} دانش‌آموز</span>
  </div>
)}

{showFsMeta && (
  <div className="book-card-meta">
    <span>⏱️ {item.duration || '—'}</span>
    <span>👥 {fsStudentCount.toLocaleString('fa-IR')} نفر</span>
  </div>
)}

{showLegacyMeta && (
  <div className="book-card-meta">
    <span>⏱️ {item.duration || "۲۰ دقیقه"}</span>
    <span>👥 {students}+</span>
  </div>
)}

<div className="book-card-title">{item.title}</div>

      {/* 🆕 نمایش قیمت فقط برای کتاب کار قفل‌شده */}
      {isWorkbook && !hasValidLicense && pricing && (
        <div className="wb-price-row">
          <span className="wb-price-coin">🪙 {pricing.coinPrice.toLocaleString('fa-IR')}</span>
          <span className="wb-price-sep">|</span>
          <span className="wb-price-toman">{pricing.tomanPrice.toLocaleString('fa-IR')} تومان</span>
        </div>
      )}

      <div className="book-card-footer">
        <div className="progress">
          <svg viewBox="0 0 44 44">
            <circle cx="22" cy="22" r={R} stroke="#e2e8f0" strokeWidth="4" fill="none" />
            <circle
              cx="22" cy="22" r={R}
              stroke="url(#progressGradient)"
              strokeWidth="4" fill="none"
              strokeLinecap="round"
              strokeDasharray={CIRC}
              strokeDashoffset={dashOffset}
              transform="rotate(-90 22 22)"
              style={{ transition: 'stroke-dashoffset 0.7s cubic-bezier(0.2, 0.9, 0.4, 1.1)' }}
            />
          </svg>
          <span>{safeProg}%</span>
        </div>
        <button onClick={() => handleViewLesson(item)}>
          {buttonLabel}
        </button>
      </div>
    </div>
  );
};

  const SectionRow = ({ section }) => (
    <div className="section-row">
      <div className="section-header">
        <h3>{section.title}</h3>
        {section.items.length > 0 && <button onClick={() => setExpandedSectionId(section.id)}>مشاهده همه →</button>}
      </div>
      <div className="section-scroll">
        <div className="scroll-inner">
          {section.items.length ? section.items.map(i => <BookCard key={getItemKey(i)} item={i} />) :
            <div className="empty"><span>🎁</span><p>به زودی</p></div>}
        </div>
      </div>
    </div>
  );

  const activeCat = BOOK_CATEGORIES.find(c => c.id === activeCategory);

  return (
    <div className={`books-page ${isDark ? 'dark' : ''}`}>

      {/* 🆕 گرادیان سراسری برای همه‌ی نوارهای پیشرفت */}
      <svg
        width="0"
        height="0"
        style={{ position: 'absolute', pointerEvents: 'none' }}
        aria-hidden="true"
        focusable="false"
      >
        <defs>
          <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#10B981" />
            <stop offset="100%" stopColor="#34D399" />
          </linearGradient>
        </defs>
      </svg>

      {/* ===== ✅ دکمه شناور سوییچ تم ===== 
      <button
        onClick={() => setIsDark(!isDark)}
        style={{
          position: 'fixed',
          top: 20,
          left: 20,
          zIndex: 9999,
          width: 48,
          height: 48,
          borderRadius: '50%',
          border: 'none',
          fontSize: 22,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: isDark
            ? 'linear-gradient(135deg, #a78bfa, #f472b6)'
            : 'linear-gradient(135deg, #FFD93D, #FF9F43)',
          boxShadow: isDark
            ? '0 6px 24px rgba(167,139,250,0.6), 0 0 30px rgba(167,139,250,0.4)'
            : '0 6px 24px rgba(255,159,67,0.5), 0 0 20px rgba(255,159,67,0.3)',
          transition: 'all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'scale(1.15) rotate(-15deg)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'scale(1) rotate(0deg)';
        }}
        title={isDark ? 'تم روشن' : 'تم تاریک'}
      >
        {isDark ? '☀️' : '🌙'}
      </button>*/}

      {/* ========== هدر پیشرفته 2026 ========== */}

      {/* هدر شفق قطبی پیشرفته (نسخه شاهکار) */}
      <div className="hero-aurora">
        {/* ذرات شناور - ۸ عدد با رنگ‌بندی متنوع */}
        <div className="particle p1"></div>
        <div className="particle p2"></div>
        <div className="particle p3"></div>
        <div className="particle p4"></div>
        <div className="particle p5"></div>
        <div className="particle p6"></div>
        <div className="particle p7"></div>
        <div className="particle p8"></div>

        {/* گوی‌های نوری پس‌زمینه */}
        <div className="glow-orb orb1"></div>
        <div className="glow-orb orb2"></div>

<div className="hero-aurora-content">
  <div className="alien-icon-big">
    <img src="/images/header-book.png" alt="کتاب‌خونه جادویی" />
  </div>
  <div className="hero-text-block">
    <div className="aurora-title-right">کتاب‌خونه‌ی جادویی</div>
    <div className="rainbow-line-short"></div>
    <div className="subtitle-right">هر کتاب یه ماجراجویی تازه</div>
  </div>
</div>

      </div>

      <header className="header">

        {/* اسلایدر سه‌بعدی 
        <div className="slider-modern">
          <div className="slider-container">
            {BOOKS_SLIDES.map((s, i) => (
              <div key={s.id} className={`slide ${i === activeSlide ? 'active' : ''}`} style={{ backgroundImage: `url(${s.image})` }}>
                <div className="overlay"></div>
                <div className="text">
                  <h2>{s.title}</h2>
                  <p>{s.subtitle}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="dots">
            {BOOKS_SLIDES.map((_, i) => <button key={i} className={`dot ${i === activeSlide ? 'active' : ''}`} onClick={() => setActiveSlide(i)} />)}
          </div>
        </div>*/}

        <BooksSlider />

        {/* جستجوی هوشمند */}
        {/* ========== جستجوی شگفت‌انگیز کودکانه (نسخه نهایی) ========== */}
        <div className="search-area">
          <div className="search-bar">
            <span className="icon">🔍</span>
            <input
              ref={searchInputRef}
              type="text"
              placeholder="دنبال یه ماجراجویی جدید می‌گردی..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button className="clear" onClick={() => setSearchQuery('')}>
                ✕
              </button>
            )}
          </div>

          {/* حباب‌های پیشنهاد جستجو (کلیک‌کن، پر می‌شه) */}
          <div className="suggested-bubbles">
          </div>
        </div>

        {/* نوار دسته‌بندی مدرن */}
        {/* لیست دسته‌بندی‌ها - ۵ تایی در یک ردیف (نسخه کارت‌های مبارزه‌ای) */}
        <div className="modal-modern-list">
          {BOOK_CATEGORIES.map((cat, index) => (
            <button
              key={cat.id}
              className={`battle-card ${activeCategory === cat.id ? 'active' : ''}`}
              style={{
                '--cat-color': cat.color,
                '--glow-color': cat.color + '80',
                animationDelay: `${index * 0.08}s`
              }}
              onClick={() => handleCategorySelect(cat.id)}
            >
              {/* تاج طلایی */}
              <div className="crown1">👑</div>

              {/* آیکون شخصیت */}
              <div className="card-character">{cat.icon}</div>

              {/* نام کارت */}
              <div className="card-name">{cat.label}</div>

              {/* سطح کارت
              <div className="card-level">
                {activeCategory === cat.id ? '⭐ سطح MAX' : '🌟 سطح ۱'}
              </div>*/}
            </button>
          ))}
        </div>
      </header>

      {/* 🆕 بنر باریک — دعوت معلم به ساخت کلاس (فقط در دسته‌ی «درس‌ها») */}
{activeCategory === 'ai-learning' && user?.role === 'teacher' && (
  <div className="books-teacher-cta">
    <button
      type="button"
      className="books-teacher-cta-inner"
      onClick={() => navigate('/teacher/books')}
    >
      {/* Orbs نورانی */}
      <span className="btc-orb btc-orb-1" aria-hidden="true" />
      <span className="btc-orb btc-orb-2" aria-hidden="true" />

      {/* جرقه‌های شناور */}
      <span className="btc-particle btc-particle-1" aria-hidden="true">✨</span>
      <span className="btc-particle btc-particle-2" aria-hidden="true">⭐</span>
      <span className="btc-particle btc-particle-3" aria-hidden="true">💫</span>

      {/* آیکون معلم با تاج */}
      <div className="btc-icon-wrap">
        <div className="btc-icon-ring" aria-hidden="true" />
        <span className="btc-icon" aria-hidden="true">🎓</span>
        
      </div>

      {/* محتوا */}
      <div className="btc-body">
        <span className="btc-badge">✨ ویژه معلمان</span>
        <h3 className="btc-title">کلاس اختصاصی خودت رو بساز!</h3>
        <p className="btc-desc">
          محتوای آموزشی‌ات رو با هزاران دانش‌آموز به اشتراک بذار و از محتوای خودت درآمد کسب کن
        </p>
        <div className="btc-benefits">
          <span className="btc-benefit">👥 دانش‌آموزان بیشتر</span>
          <span className="btc-benefit">💰 درآمد پایدار</span>
          <span className="btc-benefit">🎯 محتوای خودت</span>
        </div>
      </div>

      {/* CTA */}
      <div className="btc-cta">
        <span>شروع کن</span>
        <span className="btc-cta-arrow" aria-hidden="true">←</span>
      </div>
    </button>
  </div>
)}




      {/* 🎬 استودیوی مطالعه آزاد — طراحی سینمایی متفاوت */}
      {activeCategory === 'non-school' && user?.role === 'teacher' && (
        <div className="books-studio-cta">
          <button
            type="button"
            className="books-studio-cta-inner"
            onClick={() => navigate('/teacher/free-study')}
          >
            {/* نوار فیلم بالا و پایین */}
            <span className="bsc-filmstrip bsc-filmstrip-top" aria-hidden="true">
              <i /><i /><i /><i /><i /><i /><i /><i />
            </span>
            <span className="bsc-filmstrip bsc-filmstrip-bottom" aria-hidden="true">
              <i /><i /><i /><i /><i /><i /><i /><i />
            </span>

            {/* حلقه‌ی ریل + آیکون کلیت‌بورد */}
            <div className="bsc-reel">
              <div className="bsc-reel-ring" aria-hidden="true" />
              <span className="bsc-reel-icon" aria-hidden="true">🎬</span>
              <span className="bsc-rec" aria-hidden="true">
                <span className="bsc-rec-dot" />
                REC
              </span>
            </div>

            {/* محتوا */}
            <div className="bsc-body">
              <span className="bsc-eyebrow">🎥 استودیوی سازنده</span>
              <h3 className="bsc-title">
                محتوای ویدیویی خودت رو <span className="bsc-highlight">منتشر کن</span>
              </h3>
              <p className="bsc-sub">
                دوره بساز · ویدیو آپلود کن · با دانش‌آموزان به اشتراک بذار
              </p>
              <div className="bsc-stats">
                <span><strong>∞</strong> فضای محتوا</span>
                <span className="bsc-sep">·</span>
                <span>💰 درآمد مستقیم</span>
                <span className="bsc-sep">·</span>
                <span>👥 مخاطب واقعی</span>
              </div>
            </div>

            {/* CTA */}
            <div className="bsc-cta">
              <span className="bsc-cta-play" aria-hidden="true">
                <svg viewBox="0 0 24 24" width="16" height="16">
                  <path d="M8 5v14l11-7z" fill="currentColor" />
                </svg>
              </span>
              <span className="bsc-cta-label">ساخت درس برای مطالعه آزاد</span>
            </div>
          </button>
        </div>
      )}



      {/* ========== بخش اصلی (کتاب‌ها) ========== */}
      <main className="main">
        {sections.length > 0 ? (
          sections.map(s => <SectionRow key={s.id} section={s} />)
        ) : !userGrade && GRADE_AWARE_CATEGORIES.includes(activeCategory) ? (
          <div className="no-result">
            <span>🎓</span>
            <p>برای دیدن کتاب‌های پایه‌ات، ابتدا پایه تحصیلی‌ات را تنظیم کن</p>
          </div>
        ) : (
          <div className="no-result">
            <span>🔍</span>
            <p>نتیجه‌ای یافت نشد</p>
          </div>
        )}

        {/* ✅ دکمه «دیگر پایه‌ها» — فقط برای دسته‌های وابسته به پایه */}
        {userGrade && GRADE_AWARE_CATEGORIES.includes(activeCategory) && (
          <>
            <div className="other-grades-toggle-wrap">
              <button
                type="button"
                className={`other-grades-toggle ${showOtherGrades ? 'active' : ''}`}
                onClick={() => setShowOtherGrades(prev => !prev)}
                aria-expanded={showOtherGrades}
              >
                <span className="og-icon">{showOtherGrades ? '▲' : '▼'}</span>
                <span className="og-text">
                  {showOtherGrades ? 'بستن سایر پایه‌ها' : 'دیگر پایه‌ها'}
                </span>
              </button>
            </div>

            {showOtherGrades && (
              otherGradeSections.length > 0 ? (
                <div className="other-grades-sections">
                  {otherGradeSections.map(s => (
                    <SectionRow key={`other-${s.id}`} section={s} />
                  ))}
                </div>
              ) : (
                <div className="no-result">
                  <span>🎓</span>
                  <p>پایه‌ی دیگری برای نمایش وجود ندارد</p>
                </div>
              )
            )}
          </>
        )}
      </main>

      {/* مودال دسته‌بندی (دراور) */}
      {/* مودال دسته‌بندی مدرن (Centre Modal) */}
      {isCategoryDrawerOpen && (
        <div className="modal-modern-backdrop" onClick={() => setIsCategoryDrawerOpen(false)}>
          <div className="modal-modern-container" onClick={e => e.stopPropagation()}>
            {/* ذرات تزئینی برای جذابیت بیشتر */}
            <div className="modal-particle p1"></div>
            <div className="modal-particle p2"></div>
            <div className="modal-particle p3"></div>

            <div className="modal-modern-header">
              <h3>✨ دسته‌بندی کتاب‌ها ✨</h3>
              <button className="modal-modern-close" onClick={() => setIsCategoryDrawerOpen(false)}>
                ✕
              </button>
            </div>
            <div className="modal-modern-list">
              {BOOK_CATEGORIES.map(cat => (
                <button
                  key={cat.id}
                  className={`modal-modern-item ${activeCategory === cat.id ? 'active' : ''}`}
                  style={{ '--cat-color': cat.color }}
                  onClick={() => handleCategorySelect(cat.id)}
                >
                  <span className="item-icon">{cat.icon}</span>
                  <span className="item-label">{cat.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* مودال بیشتر */}
      {expandedSectionId && (() => {
        // ✅ در هر دو آرایه جستجو می‌کنیم تا کلیک روی «مشاهده همه» در بخش سایر پایه‌ها هم کار کند
        const foundSection =
          sections.find(s => s.id === expandedSectionId) ||
          otherGradeSections.find(s => s.id === expandedSectionId);
        if (!foundSection) return null;
        return (
          <div className="modal-backdrop" onClick={() => setExpandedSectionId(null)}>
            <div className="modal-more" onClick={e => e.stopPropagation()}>
              <button className="close" onClick={() => setExpandedSectionId(null)}>✕</button>
              <h3>{foundSection.title}</h3>
              <div className="grid-more">
                {foundSection.items.map(i => <BookCard key={getItemKey(i)} item={i} isLarge />)}
              </div>
            </div>
          </div>
        );
      })()}
      {/* مودال خرید کتاب کار */}
<WorkbookPurchaseModal
  open={purchaseModal.open}
  book={purchaseModal.book}
  userId={user?.id}
  isRenewal={purchaseModal.isRenewal}
  onClose={() => setPurchaseModal({ open: false, book: null, isRenewal: false })}
/>

      {/* 🆕 مودال تایید دانلود کتاب درسی */}
      {downloadModal.open && downloadModal.book && (
        <div className="download-modal-backdrop" onClick={() => setDownloadModal({ open: false, book: null })}>
          <div className="download-modal-box" onClick={(e) => e.stopPropagation()}>
            <div className="download-modal-icon">📥</div>
            <h3 className="download-modal-title">دانلود کتاب درسی</h3>
            <p className="download-modal-desc">
              آیا می‌خواهید کتاب <strong>«{downloadModal.book.title}»</strong> را دانلود کنید؟
            </p>
            <div className="download-modal-actions">
              <button 
                className="btn-download-cancel" 
                onClick={() => setDownloadModal({ open: false, book: null })}
              >
                انصراف
              </button>
              <button 
                className="btn-download-confirm" 
                onClick={handleConfirmDownload}
              >
                <span>شروع دانلود</span>
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}